// Head-to-head "battle it out" sound (Jack, 2026-11-03): both raiders whoosh
// in from the sides, two war-drum hits, then the VS slams down with a deep
// boom and a sword clash. Plain WebAudio, nothing to load. `slamAt` is when
// the VS lands, in seconds from now.
export function playH2HSound(slamAt = 0.8) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return () => {};
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const t0 = ctx.currentTime + 0.03;
    const out = ctx.createGain();
    out.gain.value = 0.9;
    out.connect(ctx.destination);
    const noiseBuf = (secs) => {
      const n = Math.floor(ctx.sampleRate * secs);
      const b = ctx.createBuffer(1, n, ctx.sampleRate);
      const d = b.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      return b;
    };
    const pan = (p) => {
      if (!ctx.createStereoPanner) return out;
      const s = ctx.createStereoPanner();
      s.pan.value = p;
      s.connect(out);
      return s;
    };
    // A whoosh: noise through a band-pass sweeping up, panned in from a side.
    const whoosh = (at, side) => {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf(0.6);
      const f = ctx.createBiquadFilter();
      f.type = "bandpass";
      f.Q.value = 1.2;
      f.frequency.setValueAtTime(300, at);
      f.frequency.exponentialRampToValueAtTime(2400, at + 0.45);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.35, at + 0.25);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.55);
      src.connect(f); f.connect(g); g.connect(pan(side));
      src.start(at); src.stop(at + 0.6);
    };
    // A drum: a sine dropping in pitch.
    const drum = (at, f0, f1, len, vol) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f0, at);
      o.frequency.exponentialRampToValueAtTime(f1, at + len);
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(vol, at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, at + len);
      o.connect(g); g.connect(out);
      o.start(at); o.stop(at + len + 0.05);
    };
    // A sword clash: bright, slightly out-of-tune partials that ring out,
    // plus a short scrape of noise.
    const clash = (at) => {
      [1870, 2530, 3310, 4420, 5980].forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = i % 2 ? "triangle" : "sine";
        o.frequency.setValueAtTime(f, at);
        g.gain.setValueAtTime(0.0001, at);
        g.gain.exponentialRampToValueAtTime(0.09 / (1 + i * 0.4), at + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, at + 0.9 - i * 0.1);
        o.connect(g); g.connect(out);
        o.start(at); o.stop(at + 1);
      });
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf(0.25);
      const f = ctx.createBiquadFilter();
      f.type = "highpass";
      f.frequency.value = 3000;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.25, at);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
      src.connect(f); f.connect(g); g.connect(out);
      src.start(at); src.stop(at + 0.25);
    };
    whoosh(t0, -0.8);
    whoosh(t0 + 0.12, 0.8);
    drum(t0 + slamAt - 0.36, 160, 70, 0.22, 0.5);
    drum(t0 + slamAt - 0.18, 160, 70, 0.22, 0.5);
    const s = t0 + slamAt;
    drum(s, 120, 38, 0.9, 0.9); // the boom
    clash(s + 0.02);
    // A rising "let's go" stab after the slam.
    [[262, 0.35], [392, 0.47], [523, 0.59]].forEach(([f, d]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(f, s + d);
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 1800;
      g.gain.setValueAtTime(0.0001, s + d);
      g.gain.exponentialRampToValueAtTime(0.08, s + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, s + d + (d > 0.5 ? 0.6 : 0.14));
      o.connect(lp); lp.connect(g); g.connect(out);
      o.start(s + d); o.stop(s + d + 0.7);
    });
    const close = setTimeout(() => ctx.close().catch(() => {}), (slamAt + 2.5) * 1000);
    // Skipping the intro fades the sound out.
    return () => {
      clearTimeout(close);
      try {
        out.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
        setTimeout(() => ctx.close().catch(() => {}), 400);
      } catch (e) { /* closed already */ }
    };
  } catch (e) {
    return () => {};
  }
}
